/**
 * The rail every console shares, and the one fact that tells them apart.
 *
 * Three consoles had grown three copies of the same `<nav className="rail">`,
 * each a little different: one with a theme switch and two without, one whose
 * identity opened Account and one whose did not. They were also pixel-identical
 * to look at, so nothing on the screen said whether this was the platform's
 * console, an operator's, one brand's, or an affiliate's.
 *
 * `data-portal` on `<html>` is that fact. The stylesheet reads it for the
 * accent, the mark and the density (see DESIGN.md §3); everything else is one
 * system.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent, type ReactNode } from 'react';

import { Icon, type IconName } from './icons.tsx';
import { kitText } from './messages.ts';
import { THEME_KEY, readStoredTheme, type ThemeChoice } from './theme-storage.ts';

export type Portal = 'ams' | 'operator' | 'brand' | 'affiliate';

/**
 * Which console this page is. Each app's `index.html` already names it, so the
 * first paint is right; this is for the one console that changes its mind —
 * the operator's, when a brand is locked, becomes the brand's.
 */
export function usePortal(portal: Portal): void {
  useLayoutEffect(() => {
    document.documentElement.dataset.portal = portal;
  }, [portal]);
}

/**
 * The top of the rail: which console, and whose.
 *
 * `context` is the tenant this console is about — the operator, the brand —
 * which the old rail put on the signed-in person's role line, where it read
 * as a fact about them rather than about the screen.
 *
 * The tile draws the console's own /favicon.svg: the KD Affiliate logo,
 * so the rail, the sign-in page and the browser tab carry the same mark.
 */
export function PortalMark({
  product,
  context,
  onOpen,
  label,
  current,
  logoUrl,
}: {
  portal: Portal;
  product: string;
  context?: string;
  /** Makes the mark a button — a brand console opens the brand's profile from it. */
  onOpen?: () => void;
  /** The button's accessible name, in the console's own language. */
  label?: string;
  current?: boolean;
  /** A loadable URL of the brand's logo; shown in the tile in place of the icon. */
  logoUrl?: string;
}) {
  const inner = (
    <>
      {logoUrl ? (
        <span className="portal-mark-tile has-logo">
          <img src={logoUrl} alt={context ?? product} />
        </span>
      ) : (
        <span className="portal-mark-tile is-favicon" aria-hidden="true">
          <img src="/favicon.svg" alt="" />
        </span>
      )}
      <span className="portal-mark-text">
        <span className="portal-mark-product">{product}</span>
        {context ? (
          <span className="portal-mark-context">{context}</span>
        ) : null}
      </span>
    </>
  );
  if (!onOpen) return <div className="portal-mark">{inner}</div>;
  return (
    <button
      type="button"
      className="portal-mark portal-mark-button"
      onClick={onOpen}
      {...(label ? { title: label } : {})}
      {...(current ? { 'aria-current': 'page' as const } : {})}
    >
      {inner}
    </button>
  );
}

/**
 * The rail. On a desk it is a column; on a phone it is a bar with a menu.
 *
 * Below 860px the old rail stacked above the page, so the first screen of
 * every page was the navigation and the page itself began below the fold. Here
 * the bar keeps the mark and whatever `head` carries (the notifications bell)
 * in view, and the rest opens as a sheet.
 *
 * Any button or link activated anywhere in the nav closes the sheet — a
 * destination was chosen — except the menu button itself (it already opens
 * and closes the sheet on its own terms) and anything marked `data-keep-open`,
 * like the theme switch, which changes the page the reader is still looking
 * at.
 */
export function Rail({
  head,
  children,
  foot,
}: {
  head: ReactNode;
  children: ReactNode;
  foot: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [sheetTop, setSheetTop] = useState(0);
  const bodyId = useId();
  const nav = useRef<HTMLElement>(null);
  const bar = useRef<HTMLDivElement>(null);
  const menuButton = useRef<HTMLButtonElement>(null);

  /*
   * The sheet opens from wherever the bar actually is. A banner above it (a
   * brand's scope, an impersonation notice) wraps to two lines on a phone, so
   * any fixed offset was wrong for somebody; measured, it cannot be. The page
   * holds still while the sheet is open, so the measurement stays true.
   *
   * It is also modal while it lasts: `<main>` (and the affiliate's own right
   * rail, when there is one) sit `inert` — unreachable by Tab, invisible to a
   * screen reader — rather than merely covered by the sheet, and closing it
   * (Escape or a choice) hands focus back to the button that opened it, the
   * way a dialog would.
   */
  useLayoutEffect(() => {
    if (!open) return;
    setSheetTop(bar.current?.getBoundingClientRect().bottom ?? 0);
    const root = document.documentElement;
    const before = root.style.overflow;
    root.style.overflow = 'hidden';

    const shell = nav.current?.parentElement;
    const inerted: HTMLElement[] = [];
    if (shell) {
      for (const child of Array.from(shell.children)) {
        if (child !== nav.current) {
          (child as HTMLElement).inert = true;
          inerted.push(child as HTMLElement);
        }
      }
    }

    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => {
      root.style.overflow = before;
      window.removeEventListener('keydown', onKey);
      for (const el of inerted) el.inert = false;
      menuButton.current?.focus();
    };
  }, [open]);

  /*
   * The sheet is a phone affordance. A reader who opens it and then widens the
   * window — or rotates a tablet — lands on the desktop rail, where the menu
   * button that would have closed it is gone, so the sheet and the scroll
   * lock it holds would otherwise outlive the layout that made it necessary.
   */
  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 861px)');
    const onChange = (e: MediaQueryListEvent) => { if (e.matches) setOpen(false); };
    desktop.addEventListener('change', onChange);
    return () => desktop.removeEventListener('change', onChange);
  }, []);

  /*
   * The sticky chrome above the rail, measured rather than assumed.
   * `--banner-h` was a constant in styles.css, which was wrong the moment a
   * brand's name was long enough to wrap its scope bar onto two lines; the
   * phone top bar (`.rail-top`) never had a variable of its own at all, so
   * `.subnav` and `.dashboard-aside` slid under it below 860px. Both custom
   * properties live on `<html>` so every screen can read them, not only this
   * one, and both are removed on unmount so a page that renders no Rail falls
   * back to the plain CSS in styles.css.
   */
  useLayoutEffect(() => {
    const root = document.documentElement;
    const phone = window.matchMedia('(max-width: 860px)');
    const banner = document.querySelector<HTMLElement>('.brand-scope-banner, .impersonation-banner');

    const roBanner = new ResizeObserver(([entry]) => {
      if (entry) root.style.setProperty('--banner-h', `${entry.contentRect.height}px`);
    });
    if (banner) roBanner.observe(banner);

    const roBar = new ResizeObserver(([entry]) => {
      if (entry && phone.matches) root.style.setProperty('--bar-h', `${entry.contentRect.height}px`);
      else root.style.removeProperty('--bar-h');
    });
    if (bar.current) roBar.observe(bar.current);

    // Belt and braces for the moment the breakpoint itself is crossed without
    // the bar's own box changing size.
    const onPhoneChange = () => { if (!phone.matches) root.style.removeProperty('--bar-h'); };
    phone.addEventListener('change', onPhoneChange);

    return () => {
      roBanner.disconnect();
      roBar.disconnect();
      phone.removeEventListener('change', onPhoneChange);
      root.style.removeProperty('--banner-h');
      root.style.removeProperty('--bar-h');
    };
  }, []);

  const closeOnChoice = (e: MouseEvent<HTMLElement>) => {
    if (!open) return;
    const control = (e.target as HTMLElement).closest('button, a[href]');
    if (!control || control === menuButton.current || control.closest('[data-keep-open]')) return;
    setOpen(false);
  };

  return (
    <nav
      ref={nav}
      className="rail"
      onClick={closeOnChoice}
      {...(open ? { 'data-open': 'true', style: { '--sheet-top': `${sheetTop}px` } as CSSProperties } : {})}
    >
      <div className="rail-top" ref={bar}>
        {head}
        <button
          ref={menuButton}
          type="button"
          className="rail-menu-button"
          aria-expanded={open}
          aria-controls={bodyId}
          aria-label={kitText(open ? 'shell.menu.close' : 'shell.menu.open')}
          onClick={() => setOpen((o) => !o)}
        >
          <Icon name={open ? 'close' : 'menu'} size={18} />
        </button>
      </div>
      <div className="rail-body" id={bodyId}>
        {children}
        <div className="rail-foot">{foot}</div>
      </div>
    </nav>
  );
}

export interface NavItem<T extends string> {
  readonly id: T;
  readonly label: string;
  readonly icon: IconName;
  /** Consecutive items with the same group sit under one heading. */
  readonly group?: string;
  /**
   * Something happened here that this person has not looked at yet — a
   * dispute raised, a period locked. Drawn as a red dot after the label; it
   * clears when the screen is opened (the caller decides that).
   */
  readonly dot?: boolean;
}

/**
 * The navigation, in the order given, with a heading wherever the group
 * changes. Grouping never reorders: where a tab sits relative to its
 * neighbours was decided on purpose (see each app's `TABS`), and a heading
 * only names the run it is already in.
 */
export function NavList<T extends string>({
  items,
  current,
  onSelect,
}: {
  items: readonly NavItem<T>[];
  /** Null when the page open is not one of these — Account, notifications. */
  current: T | null;
  onSelect: (id: T) => void;
}) {
  const groups: { label: string | undefined; items: NavItem<T>[] }[] = [];
  for (const item of items) {
    const last = groups.at(-1);
    if (last && last.label === item.group) last.items.push(item);
    else groups.push({ label: item.group, items: [item] });
  }

  return (
    <div className="nav">
      {groups.map((g, i) => (
        <div
          className="nav-group"
          key={g.label ?? `group-${i}`}
          {...(g.label ? { role: 'group', 'aria-label': g.label } : {})}
        >
          {g.label ? <p className="nav-group-label" aria-hidden="true">{g.label}</p> : null}
          {g.items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => onSelect(item.id)}
              {...(current === item.id ? { 'aria-current': 'page' as const } : {})}
            >
              <Icon name={item.icon} className="glyph" />
              <span className="nav-label">{item.label}</span>
              {item.dot ? (
                <>
                  <span className="nav-dot" aria-hidden="true" />
                  <span className="sr-only">{kitText('shell.newActivity')}</span>
                </>
              ) : null}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Who is signed in, at the foot of the rail. A button when it opens Account,
 * which it does in every console now; the affiliate's used to be inert.
 */
export function RailIdentity({
  name,
  detail,
  tone,
  current,
  onOpen,
  label,
}: {
  name: string;
  /** A second line — the email, or the account's portal status. */
  detail?: string;
  /** The dot beside the detail: active, under review, ended. None, no dot. */
  tone?: 'active' | 'review' | 'ended';
  current?: boolean;
  onOpen: () => void;
  /** The accessible name — "Account", in the console's own language. */
  label: string;
}) {
  return (
    <button
      type="button"
      className="identity"
      aria-label={label}
      onClick={onOpen}
      {...(current ? { 'aria-current': 'page' as const } : {})}
    >
      <span className="avatar" aria-hidden="true">{name.slice(0, 2).toUpperCase()}</span>
      <span className="identity-text">
        <span className="identity-name">{name}</span>
        {detail ? (
          <span className="identity-role">
            {tone !== undefined ? <i className={`dot ${tone}`} /> : null}
            {/* The detail needs a box of its own to be truncated in. The row
                is a flex container — the dot sits beside the text — and a
                flex container cannot ellipsise its own text: the text becomes
                an anonymous flex item, which is clipped mid-character with
                nothing to say it was. An address ending "…solutio" reads as a
                wrong address rather than a shortened one. */}
            <span className="identity-detail">{detail}</span>
          </span>
        ) : null}
      </span>
    </button>
  );
}

const THEMES: readonly ThemeChoice[] = ['system', 'light', 'dark'];

/*
 * The choice is a convenience of this browser, not a setting of the account,
 * so it lives in localStorage — which can refuse (a private window, blocked
 * site data), and then the console simply follows the system as it always did.
 */
const storedTheme = (): ThemeChoice => readStoredTheme();

function applyTheme(choice: ThemeChoice): void {
  if (typeof document === 'undefined') return;
  if (choice === 'system') document.documentElement.removeAttribute('data-theme');
  else document.documentElement.setAttribute('data-theme', choice);
}

// Before the first render, so a reader who chose dark never sees a flash of
// light while React mounts.
applyTheme(storedTheme());

/** System, light or dark — in every console, and remembered by this browser. */
export function ThemeToggle() {
  const [theme, setTheme] = useState<ThemeChoice>(storedTheme);
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    applyTheme(theme);
    try {
      if (theme === 'system') globalThis.localStorage?.removeItem(THEME_KEY);
      else globalThis.localStorage?.setItem(THEME_KEY, theme);
    } catch {
      // Not remembered; still applied for this visit.
    }
  }, [theme]);

  /*
   * The WAI-ARIA radio pattern: one tab stop for the whole group — the
   * checked button — and the arrow keys move focus and the choice together
   * among the rest, wrapping from the last back to the first. Every button
   * being its own tab stop, with the arrows doing nothing, was three stops
   * for one choice and no keyboard way to make it.
   */
  const onKeyDown = (e: ReactKeyboardEvent<HTMLButtonElement>, index: number) => {
    let next: number;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (index + 1) % THEMES.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (index - 1 + THEMES.length) % THEMES.length;
    else return;
    e.preventDefault();
    const choice = THEMES[next]!;
    setTheme(choice);
    buttons.current[next]?.focus();
  };

  return (
    <div className="theme-switch" role="radiogroup" aria-label={kitText('shell.theme')} data-keep-open>
      {THEMES.map((choice, index) => {
        const label = kitText(`shell.theme.${choice}`);
        return (
          <button
            key={choice}
            ref={(el) => { buttons.current[index] = el; }}
            type="button"
            role="radio"
            aria-checked={theme === choice}
            aria-label={label}
            title={label}
            tabIndex={theme === choice ? 0 : -1}
            className={theme === choice ? 'is-selected' : undefined}
            onClick={() => setTheme(choice)}
            onKeyDown={(e) => onKeyDown(e, index)}
          >
            <Icon name={`theme.${choice}`} size={15} />
          </button>
        );
      })}
    </div>
  );
}
